import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { httpsCallable } from "firebase/functions";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { functions } from "@/firebase";

export const Route = createFileRoute("/dashboard/admin/debugWorkers/$workerId/")({
  component: DebugWorkerDetail,
});

interface DaySchedule {
  enabled: boolean;
  allDay: boolean;
  startTime?: string;
  endTime?: string;
}

interface WorkUnitSchedule {
  mode: "single" | "per-day";
  inherited?: boolean;
  singleSchedule?: {
    allDay: boolean;
    startTime?: string;
    endTime?: string;
    days: number[];
  };
  perDaySchedule?: {
    sunday: DaySchedule;
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
  };
}

interface WorkerWorkUnit {
  projectId: string;
  projectName: string;
  workUnitId: string;
  workUnitName: string;
  description: string;
  workersNeeded: number;
  schedule?: WorkUnitSchedule;
  workerShifts: any[] | null;
  inactive?: boolean;
}

interface WorkerInfo {
  id: string;
  name: string;
  workerCode: string | null;
  phoneNumber: string | null;
  email: string | null;
}

interface GetWorkerWorkUnitsResponse {
  success: boolean;
  error?: string;
  workUnits: WorkerWorkUnit[];
  worker: WorkerInfo | null;
}

const DAYS_OF_WEEK = [
  { index: 0, name: "Sun", fullName: "Sunday" },
  { index: 1, name: "Mon", fullName: "Monday" },
  { index: 2, name: "Tue", fullName: "Tuesday" },
  { index: 3, name: "Wed", fullName: "Wednesday" },
  { index: 4, name: "Thu", fullName: "Thursday" },
  { index: 5, name: "Fri", fullName: "Friday" },
  { index: 6, name: "Sat", fullName: "Saturday" },
];

function formatTime(time?: string): string {
  if (!time) return "--:--";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function ScheduleDisplay({ schedule }: { schedule?: WorkUnitSchedule }) {
  if (!schedule) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Clock className="h-4 w-4" />
        <span>No schedule set</span>
      </div>
    );
  }

  if (schedule.mode === "single" && schedule.singleSchedule) {
    const { allDay, startTime, endTime, days } = schedule.singleSchedule;

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Same schedule all days
          </Badge>
          {schedule.inherited && (
            <Badge variant="secondary" className="text-xs">
              Inherited
            </Badge>
          )}
        </div>

        {/* Time display */}
        <div className="flex items-center gap-3 rounded-lg bg-blue-50 px-4 py-3">
          <Clock className="h-5 w-5 text-blue-600" />
          <div>
            {allDay ? (
              <span className="font-medium text-blue-900">All Day</span>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-blue-900">
                  {formatTime(startTime)}
                </span>
                <span className="text-blue-600">→</span>
                <span className="text-lg font-semibold text-blue-900">{formatTime(endTime)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Days display */}
        <div className="flex flex-wrap gap-1">
          {DAYS_OF_WEEK.map((day) => {
            const isActive = days.includes(day.index);
            return (
              <div
                key={day.index}
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium ${
                  isActive
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-400"
                }`}
                title={day.fullName}
              >
                {day.name}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (schedule.mode === "per-day" && schedule.perDaySchedule) {
    const dayMap: Record<string, DaySchedule> = {
      sunday: schedule.perDaySchedule.sunday,
      monday: schedule.perDaySchedule.monday,
      tuesday: schedule.perDaySchedule.tuesday,
      wednesday: schedule.perDaySchedule.wednesday,
      thursday: schedule.perDaySchedule.thursday,
      friday: schedule.perDaySchedule.friday,
      saturday: schedule.perDaySchedule.saturday,
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            Different per day
          </Badge>
          {schedule.inherited && (
            <Badge variant="secondary" className="text-xs">
              Inherited
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {DAYS_OF_WEEK.map((day) => {
            const daySchedule = dayMap[day.fullName.toLowerCase()];
            const isEnabled = daySchedule?.enabled;

            return (
              <div
                key={day.index}
                className={`rounded-lg border p-2 text-center ${
                  isEnabled
                    ? "border-green-200 bg-green-50"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div
                  className={`text-xs font-medium ${
                    isEnabled ? "text-green-800" : "text-gray-400"
                  }`}
                >
                  {day.name}
                </div>
                {isEnabled && (
                  <div className="mt-1 text-xs">
                    {daySchedule.allDay ? (
                      <span className="text-green-700">All day</span>
                    ) : (
                      <div className="text-green-700">
                        <div>{formatTime(daySchedule.startTime)}</div>
                        <div className="text-green-600">↓</div>
                        <div>{formatTime(daySchedule.endTime)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      <Clock className="h-4 w-4" />
      <span>Invalid schedule</span>
    </div>
  );
}

function WorkUnitCard({ workUnit }: { workUnit: WorkerWorkUnit }) {
  return (
    <Card className="overflow-hidden transition-shadow hover:shadow-lg">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">{workUnit.workUnitName}</h3>
            <div className="mt-1 flex items-center gap-2 text-blue-100">
              <Building2 className="h-4 w-4" />
              <Link
                to="/dashboard/admin/projects/$projectId"
                params={{ projectId: workUnit.projectId }}
                className="hover:underline"
              >
                {workUnit.projectName}
              </Link>
              <ExternalLink className="h-3 w-3 opacity-70" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {workUnit.inactive ? (
              <Badge variant="destructive" className="bg-red-500/20 text-white hover:bg-red-500/30">
                Inactive
              </Badge>
            ) : (
              <Badge variant="default" className="bg-green-500/20 text-white hover:bg-green-500/30">
                Active
              </Badge>
            )}
            <div className="flex items-center gap-1 text-xs text-blue-100">
              <Users className="h-3 w-3" />
              <span>Need {workUnit.workersNeeded}</span>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="space-y-4 p-6">
        {/* Description */}
        {workUnit.description && (
          <p className="text-sm text-gray-600">{workUnit.description}</p>
        )}

        {/* Schedule Section */}
        <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
            <Calendar className="h-4 w-4" />
            Schedule
          </div>
          <ScheduleDisplay schedule={workUnit.schedule} />
        </div>

        {/* Worker Shifts (if any) */}
        {workUnit.workerShifts && workUnit.workerShifts.length > 0 && (
          <div className="rounded-lg border border-gray-100 bg-gray-50/50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
              <Briefcase className="h-4 w-4" />
              Worker Shifts ({workUnit.workerShifts.length})
            </div>
            <div className="space-y-2">
              {workUnit.workerShifts.map((shift, idx) => (
                <div
                  key={`${workUnit.workUnitId}-shift-${idx}`}
                  className="flex items-center gap-3 rounded bg-white p-2 text-sm"
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    {shift.start && shift.end ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {DAYS_OF_WEEK[shift.start.day]?.name || "?"}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span>{formatTime(shift.start.time)}</span>
                        <span className="text-gray-400">→</span>
                        <span>
                          {shift.start.day !== shift.end.day &&
                            `${DAYS_OF_WEEK[shift.end.day]?.name || "?"} `}
                          {formatTime(shift.end.time)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500">Custom shift</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* IDs (collapsible-like small text) */}
        <div className="flex gap-4 border-t border-gray-100 pt-4 text-xs text-gray-400">
          <div className="flex items-center gap-1">
            <span>Work Unit:</span>
            <code className="rounded bg-gray-100 px-1 py-0.5">{workUnit.workUnitId.slice(0, 8)}...</code>
          </div>
          <div className="flex items-center gap-1">
            <span>Project:</span>
            <code className="rounded bg-gray-100 px-1 py-0.5">{workUnit.projectId.slice(0, 8)}...</code>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DebugWorkerDetail() {
  const { workerId } = Route.useParams();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GetWorkerWorkUnitsResponse | null>(null);

  useEffect(() => {
    const fetchWorkUnits = async () => {
      setLoading(true);
      setError(null);

      try {
        const getWorkerWorkUnitsCallable = httpsCallable<
          { workerId: string },
          GetWorkerWorkUnitsResponse
        >(functions, "getWorkerWorkUnitsCallable");

        const result = await getWorkerWorkUnitsCallable({ workerId });
        setData(result.data);

        if (!result.data.success) {
          setError(result.data.error || "Failed to fetch work units");
        }
      } catch (err) {
        console.error("Error fetching work units:", err);
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkUnits();
  }, [workerId]);

  if (loading) {
    return (
      <div className="p-1 sm:p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Debug Worker</h1>
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="p-1 sm:p-6">
        <div className="mb-6 flex items-center gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Debug Worker</h1>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800">Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700">{error || data?.error || "Failed to load worker data"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const worker = data.worker;
  const workUnits = data.workUnits;

  return (
    <div className="p-1 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button
          onClick={() => router.history.back()}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">Debug Worker - {worker?.name || "Unknown"}</h1>
      </div>

      {/* Worker Info Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Worker Information
          </CardTitle>
          <CardDescription>Details from callable function response</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-sm font-medium text-gray-600">Worker ID</div>
              <p className="mt-1 font-mono text-sm">{worker?.id}</p>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Name</div>
              <p className="mt-1">{worker?.name || "-"}</p>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Worker Code</div>
              <p className="mt-1">
                {worker?.workerCode ? (
                  <code className="rounded bg-gray-100 px-2 py-1">{worker.workerCode}</code>
                ) : (
                  "-"
                )}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Phone Number</div>
              <p className="mt-1">
                {worker?.phoneNumber ? formatPhoneNumber(worker.phoneNumber) : "-"}
              </p>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Email</div>
              <p className="mt-1">{worker?.email || "-"}</p>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-600">Total Work Units</div>
              <p className="mt-1 font-semibold">{workUnits.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Units Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Assigned Work Units</h2>
          <Badge variant="outline" className="text-sm">
            {workUnits.length} total
          </Badge>
        </div>

        {workUnits.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <MapPin className="mx-auto mb-4 h-16 w-16 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900">No work units assigned</h3>
              <p className="mt-2 text-gray-500">This worker is not assigned to any work units.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {workUnits.map((workUnit) => (
              <WorkUnitCard key={workUnit.workUnitId} workUnit={workUnit} />
            ))}
          </div>
        )}
      </div>

      {/* Raw Data Debug */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-sm text-gray-500">Raw Response Data (Debug)</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="max-h-64 overflow-auto rounded bg-gray-900 p-4 text-xs text-green-400">
            {JSON.stringify(data, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
